import React, { useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { TreeCheckbox } from './TreeCheckbox';
import type { NodeState } from '../utils/treeOperations';

const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export interface TreeNodeProps<T = unknown> {
  /** Internal NodeState from treeOperations */
  node: NodeState<T>;
  /** Indentation level (0 = root) */
  level: number;
  /** Whether the node is currently expanded */
  expanded: boolean;
  /** Whether the node has children */
  hasChildren: boolean;
  /** Read-only mode — suppresses toggle, allows chevron */
  readOnly: boolean;
  /** Whether to show child count badge */
  showBadge: boolean;
  /** Number of direct children */
  childCount: number;
  /** Callback when checkbox is toggled */
  onToggle: (nodeId: string) => void;
  /** Callback when expand/collapse chevron is clicked */
  onExpand: (nodeId: string) => void;
  /** Tab index for roving tabindex pattern */
  tabIndex: number;
  /** Keyboard event handler */
  onKeyDown: (e: React.KeyboardEvent, nodeId: string) => void;
}

/**
 * TreeNode — Interaction Mode Layer.
 *
 * Renders a single node row: indent -> chevron -> checkbox -> icon -> label -> badge.
 * Handles click events and applies all ARIA attributes for accessibility.
 *
 * This component MUST NOT import useTree or Tree — it only receives
 * data and callbacks via props (architecture.md Section 2.1).
 */
function TreeNodeInner<T = unknown>(props: TreeNodeProps<T>): React.JSX.Element {
  const {
    node,
    level,
    expanded,
    hasChildren,
    readOnly,
    showBadge,
    childCount,
    onToggle,
    onExpand,
    tabIndex,
    onKeyDown,
  } = props;

  const isDisabled = node.disabled;
  const isInteractive = !isDisabled && !readOnly;

  // Indentation: 16 + level * 24 px
  const paddingLeft = `${16 + level * 24}px`;

  const handleChevronClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasChildren) {
        onExpand(node.id);
      }
    },
    [hasChildren, node.id, onExpand],
  );

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isInteractive) {
        onToggle(node.id);
      }
    },
    [isInteractive, node.id, onToggle],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      onKeyDown(e, node.id);
    },
    [node.id, onKeyDown],
  );

  // Row classes
  const rowClasses = cn(
    'flex items-center gap-3 py-1 rounded-md outline-none',
    'transition-colors duration-150',
    // Hover state — suppressed for disabled and readOnly
    isInteractive && 'hover:bg-primary-100 dark:hover:bg-[rgba(14,121,253,0.08)]',
    isInteractive && 'hover:text-primary-600 dark:hover:text-primary-300',
    // Cursor
    isDisabled && 'cursor-not-allowed',
    !isDisabled && readOnly && 'cursor-default',
  );

  // Chevron SVG rotation — visibility is controlled on the parent button
  const chevronIconClasses = cn(
    'w-4 h-4 transition-transform duration-150',
    expanded && 'rotate-90',
  );

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}
      aria-checked={
        node.indeterminate ? 'mixed' : node.checked ? 'true' : 'false'
      }
      aria-disabled={isDisabled ? 'true' : undefined}
      aria-level={level + 1}
      tabIndex={tabIndex}
      className={rowClasses}
      style={{ paddingLeft }}
      onKeyDown={handleKeyDown}
    >
      {/* Chevron — expand/collapse toggle */}
      <button
        type="button"
        data-testid="tree-node-chevron"
        className={cn(
          'flex-shrink-0 flex items-center justify-center p-0 border-0 bg-transparent',
          !hasChildren && 'invisible',
          isDisabled && 'pointer-events-none',
        )}
        onClick={handleChevronClick}
        tabIndex={-1}
        aria-hidden="true"
      >
        <ChevronRight size={16} className={chevronIconClasses} />
      </button>

      {/* Checkbox area — clickable region for toggle */}
      <button
        type="button"
        data-testid="tree-node-checkbox-area"
        className="flex-shrink-0 flex items-center justify-center p-0 border-0 bg-transparent"
        onClick={handleCheckboxClick}
        tabIndex={-1}
        aria-hidden="true"
      >
        <TreeCheckbox
          checked={node.checked}
          indeterminate={node.indeterminate}
          disabled={isDisabled}
          readOnly={readOnly}
        />
      </button>

      {/* Optional icon */}
      {node.icon != null && (
        <span
          data-testid="tree-node-icon"
          className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-content-secondary"
        >
          {node.icon}
        </span>
      )}

      {/* Label */}
      <span
        data-testid="tree-node-label"
        className={cn(
          'flex-1 truncate text-sm select-none',
          'text-content-primary dark:text-dark-content-primary',
          hasChildren ? 'font-bold' : 'font-normal',
        )}
      >
        {node.label}
      </span>

      {/* Badge — child count */}
      {showBadge && hasChildren && (
        <span
          data-testid="tree-node-badge"
          className={cn(
            'flex-shrink-0 inline-flex items-center justify-center',
            'min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium',
            'bg-bg-secondary text-content-secondary',
            'dark:bg-dark-bg-secondary dark:text-dark-content-secondary',
          )}
        >
          {childCount}
        </span>
      )}
    </div>
  );
}

/**
 * Memoized TreeNode with custom comparator.
 * Only re-renders when state-relevant props change:
 * checked, disabled, indeterminate, expanded, tabIndex.
 */
export const TreeNode = React.memo(TreeNodeInner, (prev, next) => {
  return (
    prev.node.checked === next.node.checked &&
    prev.node.disabled === next.node.disabled &&
    prev.node.indeterminate === next.node.indeterminate &&
    prev.expanded === next.expanded &&
    prev.tabIndex === next.tabIndex
  );
}) as typeof TreeNodeInner;

(TreeNode as unknown as { displayName: string }).displayName = 'TreeNode';
